// src/providers/QueryProvider.tsx
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { toast } from '@/components/ui';
import type { ReactNode } from 'react';

// Crear cliente de React Query con configuración optimizada
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      // Global error handler: show toast for ALL failed mutations
      toast.error(error.message || 'Error inesperado');
    },
  }),
  defaultOptions: {
    queries: {
      // Refetch on window focus — recovers from idle sessions by triggering
      // token refresh when user returns to the tab after being away.
      // staleTime prevents excessive refetches on quick tab switches.
      refetchOnWindowFocus: true,
      // Reintentar una vez en caso de error
      retry: 1,
      // Mantener datos stale por 2 minutos (shorter to catch expired sessions)
      staleTime: 2 * 60 * 1000,
      // Mantener cache por 10 minutos
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      // No reintentar mutations por defecto
      retry: false,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* DevTools solo en desarrollo */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

// Export del cliente para uso fuera de React
export { queryClient };
